/**
 * Global Agent Drawer Component
 * Provides omnipresent AI assistance across the app
 */
import React, { useState, useRef, useEffect } from 'react';
import { Hexagon, MessageSquare, X, Plus, History, Maximize2 } from 'lucide-react';
import { useAgent, isAgentSurfacePath, deriveRouteContext } from '../contexts/AgentContext';
import { useAgentChat, ViewSuggestion } from '../hooks/useAgentChat';
import { useNavigateToView } from '../hooks/useNavigateToView';
import { useNavigate, useLocation } from 'react-router-dom';
import { tokenManager } from '../lib/api';
import AgentChatView from './AgentChatView';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ConversationListItem {
  id: string;
  title: string;
  message_count: number;
  updated_at: string;
  program_id?: number;
  program_name?: string;
}

const GlobalAgentDrawer: React.FC = () => {
  const {
    isOpen,
    closeDrawer,
    pageContext,
    conversationId,
    setConversationId,
    startNewConversation: startNewConversationInContext,
    buildContextPayload,
    consumePendingMessage,
  } = useAgent();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToView = useNavigateToView();
  // Whether the drawer was visible on the previous render pass of the load
  // effect — a hidden→visible transition is when we (re)hydrate.
  const wasVisibleRef = useRef(false);
  // The conversation whose thread this drawer's hook instance currently holds.
  // The shared conversationId can move under us (AgentHome, the full-page
  // agent, ExecHome all set it), so we compare against this, not `messages`.
  const loadedIdRef = useRef<string | null>(null);

  // History state
  const [conversationHistory, setConversationHistory] = useState<ConversationListItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const {
    messages,
    isStreaming,
    viewSuggestions,
    actionSuggestions,
    pendingJobs,
    uploads,
    sendMessage,
    setUploadProgramContext,
    undo,
    dismissSuggestion,
    dismissActionSuggestion,
    loadConversation,
    startNewConversation,
  } = useAgentChat({
    conversationId,
    buildContextPayload,
    onViewSuggestion: (suggestion: ViewSuggestion) => {
      console.log('📍 Auto-navigating from agent:', suggestion);
      navigateToView(suggestion);
    },
    onError: (error: string) => {
      console.error('Agent error:', error);
    },
    onConversationCreated: (newConversationId: string) => {
      console.log('🆕 New conversation created:', newConversationId);
      // This hook instance is already streaming into the new conversation —
      // mark it loaded so the sync effect below doesn't reload over the stream.
      loadedIdRef.current = newConversationId;
      setConversationId(newConversationId);
    },
  });

  const isVisible = isOpen && isAgentSurfacePath(location.pathname);

  const hydrate = async (id: string) => {
    loadedIdRef.current = id;
    await loadConversation(id);
  };

  /** Does `id` belong to `programId`? (One page of that program's conversations.) */
  const conversationBelongsToProgram = async (id: string, programId: number): Promise<boolean> => {
    try {
      const response = await fetch(
        `${API_URL}/api/chat/conversations?program_id=${programId}&ordering=-updated_at`,
        { headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` } },
      );
      if (!response.ok) return false;
      const data = await response.json();
      return (data.results || []).some((c: ConversationListItem) => c.id === id);
    } catch {
      return false;
    }
  };

  const loadMostRecentConversation = async (programId?: number) => {
    try {
      const token = tokenManager.getAccessToken();
      const programParam = programId ? `program_id=${programId}&` : '';

      const response = await fetch(
        `${API_URL}/api/chat/conversations?${programParam}limit=1&ordering=-updated_at`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const mostRecent = data.results[0];
        loadedIdRef.current = mostRecent.id;
        setConversationId(mostRecent.id);
        await loadConversation(mostRecent.id);
      }
    } catch (error) {
      console.error('Error loading recent conversation:', error);
    }
  };

  // Hydration. On becoming visible (opened, or navigated onto an agent surface
  // while open): send a queued openDrawerWith message, else continue the
  // shared conversation — e.g. the one the agent just carried over from /home
  // into a workflow step — else fall back to the program's most recent one.
  // While visible, follow the shared conversationId if someone else moves it.
  React.useEffect(() => {
    const becameVisible = isVisible && !wasVisibleRef.current;
    wasVisibleRef.current = isVisible;
    if (!isVisible) return;

    if (becameVisible) {
      const pending = consumePendingMessage();
      if (pending) {
        loadedIdRef.current = conversationId;
        sendMessage(pending);
        return;
      }
      // Route-derived, not pageContext: the provider's route effect may not
      // have caught up with a same-commit navigation yet.
      const routeProgramId = deriveRouteContext(location.pathname).programId;
      if (!conversationId) {
        loadMostRecentConversation(routeProgramId);
        return;
      }
      // Our own thread (or one mid-stream in this hook) — keep it as is.
      if (isStreaming || (conversationId === loadedIdRef.current && messages.length > 0)) return;
      (async () => {
        // Keep the shared conversation if it belongs here; a leftover from an
        // unrelated surface (another program, Exec) yields to this program's
        // most recent conversation.
        if (!routeProgramId || (await conversationBelongsToProgram(conversationId, routeProgramId))) {
          await hydrate(conversationId);
        } else {
          await loadMostRecentConversation(routeProgramId);
        }
      })();
      return;
    }

    if (conversationId === loadedIdRef.current) return;
    if (conversationId) {
      hydrate(conversationId);
    } else {
      // Someone started a fresh conversation (new-chat button elsewhere,
      // AgentHome reset): drop the stale thread rather than showing it
      // under a null id.
      loadedIdRef.current = null;
      startNewConversation();
    }
  }, [isVisible, conversationId]);

  // Fetch conversation history filtered by program
  const loadConversationHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const token = tokenManager.getAccessToken();
      const programParam = pageContext.programId
        ? `?program_id=${pageContext.programId}`
        : '';

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/chat/conversations${programParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConversationHistory(data.results || []);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadConversationHistory();
    }
  }, [isOpen, pageContext.programId]);

  const handleNewConversation = () => {
    loadedIdRef.current = null;
    startNewConversationInContext();
    startNewConversation();
  };

  const handleUndo = async (editId: string) => {
    try {
      await undo(editId);
    } catch (error) {
      console.error('Failed to undo:', error);
    }
  };

  const handleSend = (text: string, files?: File[]) => {
    sendMessage(text, undefined, undefined, files);
  };

  const handleExpandToFullView = () => {
    if (!pageContext.programId) return;
    closeDrawer();
    navigate(`/programs/${pageContext.programId}/agent`);
  };

  // Only show on agent surfaces (program + standalone market); hidden on the
  // full-page agent route. AppShell reserves the matching right-hand column.
  if (!isAgentSurfacePath(location.pathname)) return null;

  if (!isOpen) return null;

  return (
    <div className="fixed right-4 top-[calc(var(--space-20)+var(--cui-banner-top))] bottom-[calc(var(--space-4)+var(--cui-banner-bottom))] w-[400px] bg-card border border-border rounded-md flex flex-col shadow-sm z-40 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Hexagon className="w-4 h-4 text-foreground shrink-0" />
          <div className="min-w-0">
            <h2 className="text-base font-medium text-foreground leading-tight">
              Overmatch Agent
            </h2>
            {pageContext.programStatus && (
              <p className="text-sm text-muted-foreground truncate">
                {pageContext.programStatus}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {pageContext.programId && (
            <button
              onClick={handleExpandToFullView}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              title="Open full view"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Conversation History"
          >
            <History className="w-4 h-4" />
          </button>
          {conversationId && (
            <button
              onClick={handleNewConversation}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              title="New Conversation"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={closeDrawer}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Close Agent"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Conversation History Panel */}
      {showHistory && (
        <div className="border-b border-border p-4 max-h-64 overflow-y-auto shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">
              {pageContext.programId ? 'Program Conversations' : 'All Conversations'}
            </h3>
            <button
              onClick={() => setShowHistory(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {isLoadingHistory ? (
            <div className="text-center py-4 text-sm text-muted-foreground">Loading...</div>
          ) : conversationHistory.length === 0 ? (
            <div className="text-center py-4 text-sm text-muted-foreground">No conversations yet</div>
          ) : (
            <div className="space-y-2">
              {conversationHistory.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => {
                    loadedIdRef.current = conv.id;
                    setConversationId(conv.id);
                    loadConversation(conv.id);
                    setShowHistory(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    conversationId === conv.id
                      ? 'bg-accent border-border'
                      : 'bg-muted/40 border-border hover:bg-accent'
                  }`}
                >
                  <div className="font-medium text-sm text-foreground truncate">
                    {conv.title}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{conv.message_count} messages</span>
                    <span>•</span>
                    <span>{new Date(conv.updated_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <AgentChatView
        variant="drawer"
        messages={messages}
        isStreaming={isStreaming}
        viewSuggestions={viewSuggestions}
        actionSuggestions={actionSuggestions}
        pendingJobs={pendingJobs}
        uploads={uploads}
        onToggleUploadContext={setUploadProgramContext}
        onSend={handleSend}
        onUndo={handleUndo}
        onDismissSuggestion={dismissSuggestion}
        onDismissActionSuggestion={dismissActionSuggestion}
        onNavigateToView={navigateToView}
      />
    </div>
  );
};

/**
 * Floating button to open the agent drawer
 */
export const AgentFloatingButton: React.FC = () => {
  const { isOpen, openDrawer } = useAgent();
  const location = useLocation();

  // Only show on agent surfaces; hide on the full-page agent route and when open.
  if (!isAgentSurfacePath(location.pathname)) return null;
  if (isOpen) return null;

  return (
    <button
      onClick={openDrawer}
      className="fixed bottom-[calc(var(--space-4)+var(--cui-banner-bottom))] right-4 z-40 flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-105"
      title="Open Overmatch Agent"
    >
      <MessageSquare className="w-5 h-5" />
      <span className="font-medium text-sm">Agent</span>
    </button>
  );
};

export default GlobalAgentDrawer;
